import {
  useFetchTemplateChecklistItemsByChecklistId,
  useFetchTemplateChecklistsByPdcId,
} from "hooks/api/templateChecklistApiHooks";
import useDialog from "hooks/dialog/useDialog";
import { useManagementTextField } from "hooks/forms/ManagementHooks";
import { useState } from "react";
import { useQueryClient } from "react-query";
import {
  createTemplateChecklist,
  createTemplateChecklistItem,
  deleteTemplateChecklist,
  deleteTemplateChecklistItem,
  updateTemplateChecklist,
  updateTemplateChecklistItem,
} from "services/api/templateChecklist/templateChecklistApi";

export const usePowerDistributionCompanyChecklist = ({
  pdcId,
  initializeLoadingToast,
  updateLoadingToast,
}) => {
  const { templateChecklistLoading, templateChecklistData } =
    useFetchTemplateChecklistsByPdcId(pdcId);
  const [isDisabled, setDisabled] = useState(false);

  const [createLoading, setCreateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const { reset: resetChecklistName, ...checklistName } = useManagementTextField("");
  const { reset: resetItemName, ...itemName } = useManagementTextField("");

  const queryClient = useQueryClient();

  const handleDeleteChecklist = async (checklistId) => {
    let deleteToastId;
    try {
      setDisabled(true);
      setDeleteLoading(true);

      deleteToastId = initializeLoadingToast({
        text: `Excluindo Checklist...`,
      });

      if (!templateChecklistData) return;

      const { status } = await deleteTemplateChecklist(checklistId);

      if (status === 200) {
        updateLoadingToast({
          id: deleteToastId,
          text: `Checklist Excluído com sucesso`,
          type: "success",
        });
      }

      queryClient.setQueryData(["templateChecklist", pdcId], null);
    } catch (error) {
      updateLoadingToast({
        id: deleteToastId,
        text: error.response.data.message ?? "Erro ao excluir Checklist, tente novamente",
        type: "error",
      });
    } finally {
      setDisabled(false);
      setDeleteLoading(false);
      handleDeleteDialogClose();
    }
  };

  const handleCreateChecklist = async () => {
    let createToastId;
    try {
      if (!checklistName.value) return;

      setCreateLoading(true);
      setDisabled(true);
      createToastId = initializeLoadingToast({
        text: `Criando Checklist...`,
      });

      const { status } = await createTemplateChecklist({
        pdcId: pdcId,
        title: checklistName.value,
      });

      if (status === 201 || status === 200) {
        updateLoadingToast({
          id: createToastId,
          text: `Checklist Criado com sucesso`,
          type: "success",
        });
      }

      queryClient.invalidateQueries({ queryKey: ["templateChecklist", pdcId] });
    } catch (error) {
      console.error(error)

      updateLoadingToast({
        id: createToastId,
        text: error.response.data.message ?? "Erro ao criar Checklist, tente novamente",
        type: "error",
      });
    } finally {
      setCreateLoading(false);
      setDisabled(false);
      handleCreateDialogClose();
    }
  };

  const handleCreateItem = async () => {
    let createToastId;
    try {
      setCreateLoading(true);
      setDisabled(true);
      createToastId = initializeLoadingToast({
        text: `Criando Item...`,
      });

      if (!templateChecklistData) return;

      const { status } = await createTemplateChecklistItem({
        checklistId: templateChecklistData.id,
        content: itemName.value,
        isChecked: false,
        order: templateChecklistData.checklistItems?.length + 1 || 1,
      });

      if (status === 200) {
        updateLoadingToast({
          id: createToastId,
          text: `Item Criado com sucesso`,
          type: "success",
        });
      }

      queryClient.invalidateQueries({
        queryKey: ["templateChecklistItems", templateChecklistData.id],
      });
    } catch (error) {
      updateLoadingToast({
        id: createToastId,
        text: error.response.data.message ?? "Erro ao criar Item, tente novamente",
        type: "error",
      });
    } finally {
      setCreateLoading(false);
      setDisabled(false);
      handleCreateDialogItemClose();
    }
  };

  const handleDeleteItem = async (itemId) => {
    let deleteToastId;
    try {
      setDisabled(true);
      deleteToastId = initializeLoadingToast({
        text: `Excluindo Item...`,
      });

      if (!templateChecklistData) return;

      const { status } = await deleteTemplateChecklistItem(itemId);

      if (status === 200) {
        updateLoadingToast({
          id: deleteToastId,
          text: `Item Excluído com sucesso`,
          type: "success",
        });
      }

      queryClient.invalidateQueries({
        queryKey: ["templateChecklistItems", templateChecklistData.id],
      });
    } catch (error) {
      updateLoadingToast({
        id: deleteToastId,
        text: error.response.data.message ?? "Erro ao excluir Item, tente novamente",
        type: "error",
      });
    } finally {
      setDisabled(false);
    }
  };

  const handleSaveChecklist = async () => {
    try {
      if (!templateChecklistData) return;

      const { status } = await updateTemplateChecklist({
        id: templateChecklistData.id,
        title: checklistName.value,
      });

      if (status === 200) {
        queryClient.invalidateQueries({ queryKey: ["templateChecklist", pdcId] });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSaveChecklistItem = async (item) => {
    try {
      if (!templateChecklistData) return;

      const { status } = await updateTemplateChecklistItem({
        id: item.id,
        content: item.content,
        isChecked: item.isChecked,
        order: item.order,
      });

      if (status === 200) {
        queryClient.invalidateQueries({
          queryKey: ["templateChecklistItems", templateChecklistData.id],
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const {
    open: createDialogOpen,
    handleClickOpen: handleCreateDialogOpen,
    handleClickClose: handleCreateDialogClose,
  } = useDialog();

  const {
    open: deleteDialogOpen,
    handleClickOpen: handleDeleteDialogOpen,
    handleClickClose: handleDeleteDialogClose,
  } = useDialog();

  const {
    open: createDialogItemOpen,
    handleClickOpen: handleCreateDialogItemOpen,
    handleClickClose: handleCreateDialogItemClose,
  } = useDialog();

  return {
    templateChecklistLoading,
    templateChecklistData,
    createLoading,
    deleteLoading,
    checklistName,
    itemName,
    handleDeleteChecklist,
    handleCreateChecklist,
    handleCreateItem,
    handleDeleteItem,
    handleSaveChecklist,
    handleSaveChecklistItem,
    createDialogOpen,
    handleCreateDialogOpen,
    handleCreateDialogClose,
    deleteDialogOpen,
    handleDeleteDialogOpen,
    handleDeleteDialogClose,
    createDialogItemOpen,
    handleCreateDialogItemOpen,
    handleCreateDialogItemClose,
    isDisabled,
  };
};
